import { useQuery } from '@tanstack/react-query'
import { generatePath, Navigate, useParams } from 'react-router'
import { ButtonLink } from '@/components/ui/button-link'
import { LinkExternal01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { eventQueryOptions } from '@/features/marquee/query-options'
import { openExternalReport } from '@/features/marquee/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

/**
 * Report tab — a signpost, not a report. Until the funnel integration lands,
 * event impact lives in an external dashboard, so the tab states that and
 * hands the operator the link out.
 *
 * Only a closed event has one, so the tab is disabled until then. Typing the
 * URL by hand is the way around a disabled tab, so the guard lives here too:
 * anything not closed goes back to the event, which lands on the tab that
 * matches its state.
 */
function MarqueeEventReport() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	const { data: event, isLoading } = useQuery(eventQueryOptions(id))

	// Waits for the detail rather than redirecting on the undefined state: the
	// decision has to be made on the real state, not on "not loaded yet".
	if (isLoading) {
		return (
			<div className='flex flex-1 items-center justify-center py-20'>
				<Spinner size={24} />
			</div>
		)
	}

	if (event?.state !== 'closed') {
		return (
			<Navigate
				replace
				to={generatePath(routes.MARQUEE_EVENT_DETAIL.handle.fullPath, {
					id: id ?? ''
				})}
			/>
		)
	}

	return (
		<div className='flex flex-col gap-6 l:px-10 px-4 pt-6 pb-10'>
			<title>{t('marquee:page_title_report')}</title>
			<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-4'>
				<div className='flex max-w-2xl flex-col gap-2'>
					<h2 className='font-semibold text-lg text-neutral-900'>
						{t('marquee:report_title')}
					</h2>
					<p className='text-neutral-500 text-sm'>
						{t('marquee:report_description')}
					</p>
					<p className='text-neutral-500 text-sm'>
						{t('marquee:report_dashboard_note')}
					</p>
				</div>
				<ButtonLink
					className='l:w-auto w-full l:self-auto self-start'
					rel='noopener noreferrer'
					target='_blank'
					to={openExternalReport(event.hashtag)}
				>
					{t('marquee:report_view_cta')}
					<LinkExternal01Icon />
				</ButtonLink>
			</div>
		</div>
	)
}

export { MarqueeEventReport }
